import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #f5f5f5 0%, #e8f0fe 100%);
  padding: 3rem;
  position: relative;
  overflow: hidden;
`;

const Title = styled(motion.h1)`
  font-size: 3.2rem;
  font-weight: 700;
  color: #042E4B;
  margin-bottom: 1rem;
  text-align: center;
  max-width: 1000px;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: #607C90;
  margin-bottom: 3rem;
  text-align: center;
  max-width: 900px;
  line-height: 1.6;
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  max-width: 1400px;
  width: 100%;
  align-items: center;
`;

const FeaturesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #042E4B, #0A4A6F);
  }
`;

const IconCircle = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #042E4B, #0A4A6F);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  box-shadow: 0 4px 12px rgba(4, 46, 75, 0.3);
  flex-shrink: 0;
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureName = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: #042E4B;
  margin-bottom: 0.5rem;
`;

const FeatureDescription = styled.div`
  font-size: 1rem;
  color: #607C90;
  line-height: 1.4;
`;

const ScreenshotSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`;

const ScreenshotFrame = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 1.2rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  max-width: 600px;
  width: 100%;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 35px;
    background: linear-gradient(90deg, #042E4B, #0A4A6F);
    border-radius: 20px 20px 0 0;
  }
  
  &::after {
    content: '● ● ●';
    position: absolute;
    top: 0;
    left: 1rem;
    height: 35px;
    display: flex;
    align-items: center;
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
    letter-spacing: 0.3rem;
  }
`;

const ScreenshotImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 12px;
  margin-top: 35px;
`;

const CollaborationBadge = styled(motion.div)`
  background: linear-gradient(135deg, #EF443C, #C43931);
  color: white;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-size: 1.2rem;
  font-weight: 600;
  margin-top: 1.5rem;
  box-shadow: 0 6px 20px rgba(239, 68, 60, 0.3);
  text-align: center;
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const Slide9_5ColaboracaoScreenshot: React.FC = () => {
  const features = [
    {
      icon: '👥',
      name: 'Gestão de Equipe',
      description: 'Organize colaboradores por departamentos e responsabilidades específicas',
    },
    {
      icon: '🔄',
      name: 'Fluxo de Trabalho',
      description: 'Acompanhe o progresso de cada membro e otimize a produtividade',
    },
    {
      icon: '💬',
      name: 'Comunicação Integrada',
      description: 'Mantenha todos alinhados com atualizações e feedbacks em tempo real',
    },
    {
      icon: '📋',
      name: 'Delegação Inteligente',
      description: 'Atribua tarefas com base no perfil e disponibilidade da equipe',
    },
  ];

  return (
    <Container>
      <Title
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        Colaboração que Transforma Equipes
      </Title>

      <Subtitle
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        Conecte sua equipe em torno de objetivos comuns e maximize o potencial coletivo
      </Subtitle>

      <MainContent>
        <FeaturesSection>
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.name}
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{
                duration: 0.4,
                delay: 0.3 + index * 0.08,
                type: 'spring',
                stiffness: 100,
              }}
            >
              <IconCircle>{feature.icon}</IconCircle>
              <FeatureContent>
                <FeatureName>{feature.name}</FeatureName>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureContent>
            </FeatureCard>
          ))}
        </FeaturesSection>

        <ScreenshotSection>
          <ScreenshotFrame
            initial={{ x: 50, opacity: 0, rotateY: -15 }}
            animate={{ x: 0, opacity: 1, rotateY: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <ScreenshotImage
              src="/screenshots/screenshot6.png"
              alt="Gestão de Colaboradores da ThinkWay"
            />
          </ScreenshotFrame>
          
          <CollaborationBadge
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.7 }}
          >
            <span>🤝</span>
            <span>Equipe Conectada</span>
          </CollaborationBadge>
        </ScreenshotSection>
      </MainContent>
    </Container>
  );
};

export default Slide9_5ColaboracaoScreenshot;